import React, { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "../../api";
import { Icon, Empty, Spinner, useToast } from "../../components/ui";
import { derive, applyRefData } from "./recordDerive";
import { WorkView } from "../user/AssignedRecords";

const dayOnly = (t) => { try { return t ? new Date(t).toLocaleDateString() : "—"; } catch { return "—"; } };

export function HelpQueue({ role }) {
  const toast = useToast();
  const isSA = role === "SUPER_ADMIN";
  const [tab, setTab] = useState("open");            // open | solved  (solved shown to super admin)
  const [open, setOpen] = useState(null);
  const [all, setAll] = useState(null);
  const [err, setErr] = useState("");
  const [working, setWorking] = useState(null);

  const load = useCallback(async () => {
    setErr("");
    try {
      const ref = await api.getRefData().catch(() => null);
      applyRefData(ref);
      const openRecs = await api.listHelpQueue();
      setOpen(openRecs);
      if (isSA) { const everything = await api.listPatientRecords().catch(() => []); setAll(everything); }
    } catch (e) { setErr(e.message); setOpen([]); }
  }, [isSA]);
  useEffect(() => { load(); }, [load]);

  const solved = useMemo(() => (all || []).filter((r) => r.helpResolvedBy), [all]);
  const rows = tab === "solved" ? solved : (open || []);
  const derived = useMemo(() => { const m = new Map(); rows.forEach((r) => m.set(r.id, derive(r))); return m; }, [rows]);

  // super-admin summaries
  const openByType = useMemo(() => { const m = {}; (open || []).forEach((r) => { m[r.needHelp] = (m[r.needHelp] || 0) + 1; }); return m; }, [open]);
  const openByUser = useMemo(() => {
    const m = {}; (open || []).forEach((r) => { const u = r.assignedUserName || "Unknown"; m[u] = (m[u] || 0) + 1; }); return m;
  }, [open]);
  const solvedBy = useMemo(() => {
    const m = {}; solved.forEach((r) => { const u = r.helpResolvedBy || "Unknown"; m[u] = (m[u] || 0) + 1; }); return m;
  }, [solved]);

  if (working) {
    return <WorkView key={working.id} record={working} role={role}
      onClose={() => setWorking(null)}
      onSaved={() => { setWorking(null); toast("Saved", "ok"); load(); }}
      onOpenNew={(rec) => setWorking(rec)} />;
  }

  return (
    <div className="helpq">
      {isSA && (
        <div className="hq-summary">
          <div className="hq-stat"><div className="hq-stat-n">{(open || []).length}</div><div className="hq-stat-l">Open requests</div></div>
          <div className="hq-stat"><div className="hq-stat-n">{openByType["Supervisor"] || 0}</div><div className="hq-stat-l">To supervisor</div></div>
          <div className="hq-stat"><div className="hq-stat-n">{openByType["Frontend"] || 0}</div><div className="hq-stat-l">To frontend</div></div>
          <div className="hq-stat"><div className="hq-stat-n">{solved.length}</div><div className="hq-stat-l">Solved (total)</div></div>
        </div>
      )}

      {isSA && (tab === "open" ? Object.keys(solvedBy).length > 0 : Object.keys(openByUser).length >= 0) && (
        <div className="hq-breakdown">
          {tab === "solved"
            ? <><b>Solved by supervisor:</b> {Object.entries(solvedBy).length ? Object.entries(solvedBy).map(([u, n]) => <span key={u} className="hq-chip">{u}: {n}</span>) : <span className="cell-sub">none yet</span>}</>
            : <><b>Open — sent by:</b> {Object.entries(openByUser).length ? Object.entries(openByUser).map(([u, n]) => <span key={u} className="hq-chip">{u}: {n}</span>) : <span className="cell-sub">none</span>}</>}
        </div>
      )}

      {isSA && (
        <div className="hq-tabs">
          <button className={"hq-tab" + (tab === "open" ? " on" : "")} onClick={() => setTab("open")}>Open <span className="hq-tab-n">{(open || []).length}</span></button>
          <button className={"hq-tab" + (tab === "solved" ? " on" : "")} onClick={() => setTab("solved")}>Solved <span className="hq-tab-n">{solved.length}</span></button>
        </div>
      )}

      <p className="muted" style={{ marginTop: 0 }}>
        {tab === "solved" ? "Help requests that were resolved, and who resolved them." : "Records a user forwarded for help. Click a row to open and work on it."}
      </p>

      <div className="card"><div className="table-wrap">
        <table className="tbl help-tbl">
          <thead><tr>
            <th>Help type</th><th>Sent by</th><th>Requested on</th><th>Reason</th>
            <th>PID</th><th>Order</th><th>Group</th><th>Payor / IPA</th>
            {tab === "solved" ? <><th>Solved by</th><th>Solved on</th></> : <th>Status</th>}
          </tr></thead>
          <tbody>
            {rows.map((r) => {
              const d = derived.get(r.id) || {};
              return (
                <tr key={r.id} className="row-click" onClick={() => setWorking(r)}>
                  <td><span className={"help-type " + (r.needHelp === "Supervisor" ? "ht-sup" : "ht-front")}>{r.needHelp || (tab === "solved" ? "—" : "")}</span></td>
                  <td className="cell-strong">{r.assignedUserName || r.workedByName || "—"}</td>
                  <td className="cell-sub">{dayOnly(r.workedAt)}</td>
                  <td className="help-reason">{r.reasonForHelp || "—"}</td>
                  <td className="mono">{r.mrn}</td>
                  <td>{d.orders}</td>
                  <td>{d.location || "—"}</td>
                  <td className="cell-sub">{d.insurance || "—"}</td>
                  {tab === "solved"
                    ? <><td className="cell-strong">{r.helpResolvedBy || "—"}</td><td className="cell-sub">{dayOnly(r.helpResolvedAt)}</td></>
                    : <td>{r.paStatus || <span className="cell-sub">—</span>}</td>}
                </tr>
              );
            })}
          </tbody>
        </table>
        {(open === null || (isSA && tab === "solved" && all === null)) && <div className="empty"><Spinner /></div>}
        {err && <div className="err-text" style={{ padding: 18 }}>{err}</div>}
        {open !== null && rows.length === 0 && !err && <Empty icon={<Icon.help width="40" height="40" />} title={tab === "solved" ? "No resolved requests yet" : "No help requests"} text={tab === "solved" ? "Resolved help requests will be listed here." : "When a user forwards a record for help, it shows up here."} />}
      </div></div>
    </div>
  );
}